import React, { useEffect, useState, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import InfiniteScroll from 'react-infinite-scroller';
import type { Content } from '@jwp/ott-common/types/config';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { mediaURL, slugify } from '@jwp/ott-common/src/utils/urlFormatting';
import { parseAspectRatio, parseTilesDelta } from '@jwp/ott-common/src/utils/collection';
import { testId } from '@jwp/ott-common/src/utils/common';
import { PLAYLIST_LIMIT, PersonalShelf } from '@jwp/ott-common/src/constants';
import usePlaylists, { type UsePlaylistResult } from '@jwp/ott-hooks-react/src/usePlaylists';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { getScopedId, useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useLocation } from 'react-router';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import _ from 'lodash';

import Shelf from '../../components/Shelf/Shelf';
import InfiniteScrollLoader from '../../components/InfiniteScrollLoader/InfiniteScrollLoader';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import CardGrid from '../../components/CardGrid/CardGrid';
import { useTheme } from '../../../../../ThemeContext';
import Button from '../../components/Button/Button';

import styles from './ShelfList.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';

const ROWS_TO_LOAD_STEP = 4;

type Props = {
  rows: Content[];
  isHubContent?: boolean;
  pageId?: string;
};

const ShelfList = ({ rows, isHubContent, pageId }: Props) => {
  const { pathname: parentPath } = useLocation();
  const { universalStyles } = useTheme();
  const { accessModel, config } = useConfigStore(({ accessModel, config }) => ({ accessModel, config }), shallow);
  const {
    Home: { rowsToLoad: INITIAL_ROWS_TO_LOAD },
    click,
    activeScreen,
  } = useContentStateStore.getState();
  const [rowsToLoad, setRowsToLoad] = useState(INITIAL_ROWS_TO_LOAD);
  const [updatedPlaylist, setUpdatedPlaylist] = useState<UsePlaylistResult>([]);
  const { t } = useTranslation('error');
  const { isContinueWatchingEnabled, isFavoriteEnable, heroBannerSize, isAnalyticsEnable } = useAppConfig();
  const isLoggedIn = !!useAccountStore(({ user }) => user);
  const [playlistRefreshKey, setPlaylistRefreshKey] = useState(0);
  const labelRef = useRef<string | undefined>();
  const breakpoint = useBreakpoint();

  const seenShelves = useRef(new Set<number>());
  const seenProgress = useRef(new Set<number>());
  const [progress, setProgress] = useState(0);
  const [showLoadMore, setShowLoadMore] = useState(true);

  const watchHistoryDictionary = useWatchHistoryStore((state) => state.getDictionaryWithSeries());

  // User
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  // Todo: move to more common package?
  // Ensure playlist is a stable
  const stableRowsToLoad = useMemo(() => Number(rowsToLoad), [rowsToLoad]);
  const isSinglePlaylist = rows?.length === 1;
  const playlists = usePlaylists(rows, stableRowsToLoad, playlistRefreshKey);

  const apiService = getModule(ApiService);

  const liveEventPlayListCall = () => {
    setPlaylistRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    return () => {
      setRowsToLoad(INITIAL_ROWS_TO_LOAD);
    };
  }, []);

  useEffect(() => {
    useContentStateStore.setState((state: any) => ({
      ...state,
      Home: {
        ...state.Home,
        rowsToLoad: rowsToLoad,
      },
    }));
  }, [rowsToLoad]);

  // If all playlists are empty (most probably due to geo restrictions), we show an empty shelves error

  const handleLastItemClick = async (idx: number) => {
    const playlistFeedId = playlists[idx].data?.feedid;
    const scopedId = getScopedId(pageId || parentPath, playlistFeedId || '');
    const contentStore = useContentStateStore.getState();
    if (!playlistFeedId) { return; }
    const shelfRow = contentStore.rows[scopedId];
    // Checking is all data fetched or not, If we get remainder after divisble by limit, then there isn't any data to fetch.
    if (!shelfRow?.hasNext) {
      return;
    }

    const nextPageOffset = shelfRow?.pageOffset + PLAYLIST_LIMIT;
    const nextPageLimit = PLAYLIST_LIMIT;
    let combinedPlayList;
    const isReels = rows[idx].custom?.mediaType === 'reels';
    let fetchedPlaylist;

    if (isReels) {
      fetchedPlaylist = await apiService.getReelsByPlaylistId(playlistFeedId, PLAYLIST_LIMIT, nextPageOffset);
    } else {
      fetchedPlaylist = await apiService.getPlaylistById(playlistFeedId, {
        page_limit: PLAYLIST_LIMIT.toString(),
        page_offset: nextPageOffset.toString(),
      });
    }

    if (fetchedPlaylist) {
      if (updatedPlaylist.length) {
        combinedPlayList = updatedPlaylist[idx].data?.playlist.concat(fetchedPlaylist?.playlist);
      } else {
        combinedPlayList = playlists[idx].data?.playlist.concat(fetchedPlaylist?.playlist);
      }

      if (combinedPlayList) {
        const res: any = source.toSpliced(idx, 1, {
          ...source[idx],
          data: {
            ...source[idx].data,
            title: source[idx].data?.title ?? '',
            playlist: combinedPlayList,
          },
        });
        setUpdatedPlaylist(res);
        setShowLoadMore(!!fetchedPlaylist.links?.next);
        contentStore.setRowState(pageId || parentPath, playlistFeedId, {
          pageOffset: nextPageOffset,
          pageLimit: nextPageLimit,
          totalLoaded: combinedPlayList?.length,
          hasNext: !!fetchedPlaylist.links?.next,
          lastFetched: Date.now(),
        });
      }
    }
  };

  const getShelfSize = (featuredImageSize: string) => {
    switch (featuredImageSize) {
      case 'small':
        return styles.shelfContainerSmall;
      default:
        return styles.shelfContainer;
    }
  };

  const source = updatedPlaylist.length ? updatedPlaylist : playlists;
  const geoDependentPlaylists = source.filter(({ data }) => data?.kind !== 'WATCHLIST');

  const allPlaylistsEmpty = geoDependentPlaylists.length > 0 && geoDependentPlaylists.every(({ data }) => !data?.playlist?.length);

  useEffect(() => {
    if (allPlaylistsEmpty) {
      useFavoritesStore.setState({ showFavourites: false });
    }
  }, [allPlaylistsEmpty]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }
    // seenShelves.current.clear();
    // seenProgress.current.clear();
    const pathname = parentPath;
    const segments = pathname.split('/');
    const contentIdFromPath = segments[2];

    const activeItem = config?.menu?.find((item) => item.contentId === contentIdFromPath);

    if (pathname === '/') {
      labelRef.current = 'Home';
    } else if (click?.label) {
      labelRef.current = click.label;
    } else if (activeItem?.label) {
      labelRef.current = activeItem?.label;
    }
  }, [rows.length, parentPath, config.menu, click, isAnalyticsEnable]);

  useEffect(() => {
    if (!isAnalyticsEnable) { return; }

    let lastScrollTop = window.scrollY || document.documentElement.scrollTop;
    let lastSentScrollTop = lastScrollTop;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'down' : 'up';
      const delta = Math.abs(scrollTop - lastSentScrollTop);

      lastScrollTop = scrollTop;

      if (delta < 100 || scrollTop <= 0) { return; }
      lastSentScrollTop = scrollTop;

      sendGAEvent('scroll', {
        scroll_direction: direction,
        userid_no: userId,
        device_type: deviceType,
      });
    }, 300);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [deviceType, userId, isAnalyticsEnable]);

  const handleShelfSeen = (index: number) => {
    if (!seenShelves.current.has(index)) {
      seenShelves.current.add(index);
      const percent = (seenShelves.current.size / rows.length) * 100;
      setProgress(percent);
    }
  };

  useEffect(() => {
    const total = rows.length;
    const seenCount = seenShelves.current.size;
    const milestones = [25, 50, 75];

    milestones.forEach((m) => {
      if ((seenCount / total) * 100 >= m && !seenProgress.current.has(m)) {
        seenProgress.current.add(m);
        sendGAEvent('scroll', {
          percent_scrolled: `${m}%`,
          scroll_direction: 'down',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    });

    if (seenCount === total - 1 && !seenProgress.current.has(100)) {
      seenProgress.current.add(100);
      sendGAEvent('scroll', {
        percent_scrolled: '100%',
        scroll_direction: 'down',
        userid_no: userId,
        device_type: deviceType,
      });
    }
  }, [progress, rows.length, deviceType, userId]);

  const renderContent = () => {
    if (allPlaylistsEmpty) {
      return <ErrorPage title={t('empty_shelves_heading')} message={t('empty_shelves_description')} alignTop={isHubContent} />;
    }

    if (isSinglePlaylist) {
      if (!playlists[0].data) { return null; }
      if (
        (rows[0].type === 'continue_watching' && !isContinueWatchingEnabled) ||
        (rows[0].type === 'favorites' && !isFavoriteEnable && !isLoggedIn) ||
        (rows[0].type === 'favorites' && !isFavoriteEnable && isLoggedIn)
      ) {
        return null;
      }

      const playlistData = updatedPlaylist.length ? updatedPlaylist[0].data : playlists[0].data;
      const isLoading = updatedPlaylist.length ? updatedPlaylist[0].isLoading : playlists[0].isLoading;
      if (!playlistData) { return null; }

      return (
        <section className={classNames(styles.shelfContainer, styles.grid)}>
          <h2 className={classNames(styles.title)} style={{ ...universalStyles.videostripTitle, ...universalStyles.secondaryFontFamily }}>
            {playlistData?.title}
          </h2>
          <CardGrid
            getUrl={(playlistItem) => mediaURL({ id: playlistItem.mediaid, title: playlistItem.title, playlistId: playlistData?.feedid })}
            playlist={playlistData}
            accessModel={accessModel}
            isLoading={isLoading || false}
            isLoggedIn={!!user}
            hasSubscription={!!subscription}
          />
          {showLoadMore && playlistData?.links?.next && (
            <div className={styles.loadMoreBtn}>
              <Button
                label={t('common:load_more')}
                aria-label={t('loadMore')}
                onClick={() => handleLastItemClick(0)}
                color={'default'}
                fullWidth={breakpoint < Breakpoint.md}
                size="large"
                type="submit"
              />
            </div>
          )}
        </section>
      );
    }

    return (
      <InfiniteScroll
        style={{ overflow: 'hidden' }}
        loadMore={() => setRowsToLoad((current) => current + ROWS_TO_LOAD_STEP)}
        hasMore={rowsToLoad < rows.length}
        loader={<InfiniteScrollLoader key="loader" />}
      >
        {rows.slice(0, rowsToLoad).map(({ type, featured, title, custom }, index) => {
          const item = updatedPlaylist.length ? updatedPlaylist[index] : playlists[index];

          const playlist = item?.data;
          const isLoading = item?.isLoading;
          const error = item?.error;

          if (!playlist?.playlist?.length) { return null; }
          const isReels = custom?.mediaType === 'reels';
          const posterAspect = isReels
            ? '2:3' : parseAspectRatio(playlist.cardImageAspectRatio || playlist.shelfImageAspectRatio);
          const visibleTilesDelta = parseTilesDelta(posterAspect);

          const isFeaturedRow = featured || playlist?.isFeatured === 'true' || playlist?.isFeatured === true;
          if (
            (type === 'continue_watching' && !isContinueWatchingEnabled) ||
            (type === 'favorites' && !isFavoriteEnable && !isLoggedIn) ||
            (type === 'favorites' && !isFavoriteEnable && isLoggedIn)
          ) {
            return null;
          }
          return (
            <section
              data-index={index}
              key={`${pageId || parentPath}_${index}_${playlist.id}`}
              className={classNames(getShelfSize(heroBannerSize), { [styles.featured]: isFeaturedRow })}
              data-testid={testId(`shelf-${isFeaturedRow ? 'featured' : type === 'playlist' ? slugify(title || playlist?.title) : type}`)}
              aria-label={title || playlist?.title}
            >
              <Shelf
                index={index}
                loading={isLoading}
                error={error}
                type={type}
                playlist={playlist}
                watchHistory={type === PersonalShelf.ContinueWatching ? watchHistoryDictionary : undefined}
                title={title || playlist?.title}
                featured={isFeaturedRow}
                accessModel={accessModel}
                isLoggedIn={!!user}
                hasSubscription={!!subscription}
                posterAspect={posterAspect}
                visibleTilesDelta={visibleTilesDelta}
                onLastItemClick={() => handleLastItemClick(index)}
                customShelfValues={custom}
                liveEventPlayListCall={liveEventPlayListCall}
                onShelfSeen={handleShelfSeen}
                clickLabel={labelRef.current ?? ''}
                activeScreen={activeScreen}
                pageId={pageId}
                isReels={isReels}
              />
            </section>
          );
        })}
      </InfiniteScroll>
    );
  };

  return <div className={styles.shelfList}>{renderContent()}</div>;
};

export default ShelfList;
